
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useEducationalAnalysis } from "@/hooks/use-educational-analysis";
import { 
  BookOpen, 
  Users, 
  AlertTriangle, 
  CheckCircle, 
  TrendingUp,
  FileText,
  Target,
  Lightbulb
} from "lucide-react";

/**
 * Comprehensive Educational Content Dashboard
 * Provides analysis, insights, and actionable recommendations for content management
 */
export const EducationalDashboard = () => {
  const { data: analysis, isLoading, error } = useEducationalAnalysis();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Error Loading Analysis</AlertTitle>
        <AlertDescription>
          Failed to load educational content analysis. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  const { statistics, contentGaps, recommendations, topics, modules, lessons } = analysis;

  // Helper function to get severity color
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  // Helper function to get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Educational Content Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Comprehensive analysis and management of your educational content
        </p>
      </div>

      {/* Overview Statistics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Content</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.totalLessons}</div>
            <p className="text-xs text-muted-foreground">
              {statistics.totalTopics} topics, {statistics.totalModules} modules
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Content Completion</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.contentCompletionRate}%</div>
            <Progress value={statistics.contentCompletionRate} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">
              {statistics.lessonsWithContent} of {statistics.totalLessons} lessons have content
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Content Issues</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{contentGaps.length}</div>
            <p className="text-xs text-muted-foreground">
              {statistics.criticalGaps} critical issues
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Structure Health</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round(((statistics.totalLessons - statistics.criticalGaps) / statistics.totalLessons) * 100)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Avg {statistics.averageLessonsPerModule} lessons per module
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="gaps">Content Gaps</TabsTrigger>
          <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Top Performing Topics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Well-Developed Topics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topics
                    .filter(t => t.contentPercentage >= 70)
                    .slice(0, 5)
                    .map(topic => (
                      <div key={topic.id} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{topic.title}</p>
                          <p className="text-xs text-gray-500">
                            {topic.lessonCount} lessons, {topic.moduleCount} modules
                          </p>
                        </div>
                        <Badge variant="secondary">
                          {topic.contentPercentage}%
                        </Badge>
                      </div>
                    ))}
                  {topics.filter(t => t.contentPercentage >= 70).length === 0 && (
                    <p className="text-sm text-gray-500">No fully developed topics yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Topics Needing Attention */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Topics Needing Attention
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topics
                    .filter(t => t.contentPercentage < 50)
                    .slice(0, 5)
                    .map(topic => (
                      <div key={topic.id} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{topic.title}</p>
                          <p className="text-xs text-gray-500">
                            {topic.lessonCount} lessons, {topic.moduleCount} modules
                          </p>
                        </div>
                        <Badge variant="destructive">
                          {topic.contentPercentage}%
                        </Badge>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Content Gaps Tab */}
        <TabsContent value="gaps" className="space-y-4">
          <div className="grid gap-4">
            {contentGaps.map((gap, index) => (
              <Alert key={index} className="border-l-4" style={{borderLeftColor: `var(--${getSeverityColor(gap.severity).replace('bg-', '')})`}}>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle className="flex items-center justify-between">
                  <span>{gap.description}</span>
                  <Badge variant={gap.severity === 'critical' ? 'destructive' : 'secondary'}>
                    {gap.severity}
                  </Badge>
                </AlertTitle>
                <AlertDescription className="mt-2">
                  <p>{gap.recommendation}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Affects {gap.affectedIds.length} item(s)
                  </p>
                </AlertDescription>
              </Alert>
            ))}
            {contentGaps.length === 0 && (
              <Card>
                <CardContent className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium">No Critical Issues Found</h3>
                    <p className="text-gray-500">Your content structure looks good!</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="space-y-4">
          <div className="grid gap-4">
            {recommendations.map((rec, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Lightbulb className="h-5 w-5" />
                      {rec.title}
                    </span>
                    <Badge variant={getPriorityColor(rec.priority) as any}>
                      {rec.priority} priority
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{rec.description}</p>
                  <div className="grid gap-2 md:grid-cols-2">
                    <div>
                      <p className="text-sm font-medium">Estimated Effort</p>
                      <p className="text-sm text-gray-500">{rec.estimatedEffort}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Expected Impact</p>
                      <p className="text-sm text-gray-500">{rec.impact}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {recommendations.length === 0 && (
              <Card>
                <CardContent className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <Target className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium">All Set!</h3>
                    <p className="text-gray-500">No specific recommendations at this time.</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-6">
          <div className="grid gap-6">
            {/* Topics Detail */}
            <Card>
              <CardHeader>
                <CardTitle>Topics Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topics.map(topic => (
                    <div key={topic.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <h4 className="font-medium">{topic.title}</h4>
                        <p className="text-sm text-gray-600">{topic.description}</p>
                        <p className="text-xs text-gray-500">
                          {topic.moduleCount} modules • {topic.lessonCount} lessons
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge variant={topic.contentPercentage >= 70 ? 'default' : topic.contentPercentage >= 30 ? 'secondary' : 'destructive'}>
                          {topic.contentPercentage}%
                        </Badge>
                        <Progress value={topic.contentPercentage} className="w-20 mt-1" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
